import React, {
  ChangeEvent,
  CSSProperties,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { useFormContext } from 'react-hook-form';

import { Throttle } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import FormErrors from '../FormError';

import './Slider.css';

type Props = {
  type: string;
  name: string;
  placeholder: string;
  label: string;
  size?: string;
  tabIndex?: number;
  disabled?: boolean;
  step?: string;
  throttle: Throttle;
};

const MIN_VAL = 0;
const MAX_VAL = 999;

const sliderStyles = (currentMax: number, currentMin: number) => {
  const includeStyle: CSSProperties = {
    background: `darkgray`,
  };
  const excludeStyle: CSSProperties = {
    background: `darkgray`,
  };

  const minPercent = (currentMin / MAX_VAL) * 100;
  const maxPercent = ((currentMax + 1) / MAX_VAL) * 100;
  const sliderStyle = {
    min: {
      left: `${minPercent - 1}%`,
    },
    max: {
      left: `${maxPercent - 3}%`,
    },
  };

  return {
    excludeStyle,
    includeStyle,
    sliderStyle,
  };
};

const Slider: FunctionComponent<Props> = ({
  label,
  name,
  size,
  type,
  disabled = false,
  throttle,
  ...rest
}) => {
  const { errors, setValue, register: reg } = useFormContext();
  const [minVal, setMinVal] = useState(throttle.min);
  const [maxVal, setMaxVal] = useState(throttle.max);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);

  // Convert to percentage
  const getPercent = (value: number) => (value / (MAX_VAL + 1)) * 100;

  // Set width of the range to decrease from the left side
  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value); // Preceding with '+' converts the value from type string to type number

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  const {
    governorRanges: {
      excludeRangeEnabled,
      excludeRange,
      includeRangeEnabled,
      includeRange,
    },
  } = throttle;

  useEffect(() => {
    reg('min');
    reg('max');
  }, []);

  const { excludeStyle, includeStyle, sliderStyle } = sliderStyles(
    maxVal,
    minVal,
  );

  const handleOnChangeMin = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(+event.target.value, maxVal);
    setMinVal(value);
    setValue('min', value);
    event.target.value = value.toString();
  };

  const handleOnChangeMax = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(+event.target.value, minVal);
    setMaxVal(value);
    setValue('max', value);
    event.target.value = value.toString();
  };

  return (
    <div
      className={classnames({
        small: size === 'small',
        medium: size === 'medium',
        large: size === 'large',
      })}
    >
      <label htmlFor={name} />
      <div className="fieldLabel">{label}</div>
      <div className="container">
        {includeRangeEnabled && (
          <div className="rounded-left-border" style={includeStyle}>
            <span className="left-range-label">
              {Math.round(includeRange.max / 10)}%
            </span>
          </div>
        )}
        <input
          className={classnames('thumb', 'min', 'z-index-3', {
            disabled,
            range: type === 'range',
            'rounded-left-border': !includeRangeEnabled,
          })}
          name="min"
          ref={minValRef}
          onChange={handleOnChangeMin}
          disabled={disabled}
          type={type}
          id="min"
          min={MIN_VAL}
          max={MAX_VAL}
          value={minVal}
          {...rest}
        />
        <input
          className={classnames('thumb', 'max', 'z-index-4', {
            disabled,
            range: type === 'range',
            'rounded-right-border': !excludeRangeEnabled,
          })}
          name="max"
          ref={maxValRef}
          onChange={handleOnChangeMax}
          disabled={disabled}
          type={type}
          id="max"
          min={MIN_VAL}
          max={MAX_VAL}
          value={maxVal}
          {...rest}
        />
        <div className="slider">
          <div className="slider__track"></div>
          <div ref={range} className="slider__range"></div>
          <span style={sliderStyle.min} className="slider__left-value">
            {minVal / 10} %
          </span>
          <span style={sliderStyle.max} className="slider__right-value">
            {(maxVal + 1) / 10} %
          </span>
        </div>
        {excludeRangeEnabled && (
          <div className="rounded-right-border" style={excludeStyle}>
            <span className="right-range-label">
              {Math.round(excludeRange.min / 10)}%
            </span>
          </div>
        )}
      </div>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};
export default Slider;
